import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { createPortal } from 'react-dom'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { useGetFormTemplatePreviewMutation } from '../../../store/ai/formTemplate'
import { useExecuteSystemPresetMutation } from '../../../store/ai/aiExecute'
import { useGetAllScorecardsQuery } from '../../../store/ai/scorecard'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { FileInfo } from '../../../store/media/file'

import { ProcessDocumentFileUploadProgress, ProgressItem } from '../../ProcessDocument/ProcessDocumentFileUploadProgress/ProcessDocumentFileUploadProgress'
import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'
import { useSafeTimeout } from '../../ProcessDocument/hooks'

import { FormTemplateMappingFormValues, FormTemplateMappingSource, FormTemplatePreview } from '../../../models/FormTemplate'
import { ObjectDescriptor, Scorecard } from '../../../models/Scorecard'
import { StructuredSummaryResult } from '../../../models/AiResult'

import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

import { getContextForFormTemplateMappingSuggestion, isResultValidMappingSuggestion } from './utils'
import { FormTemplateMappingForm } from './FormTemplateMappingForm'
import { FormTemplateViewHeader } from './FormTemplateViewHeader'
import { FormTemplateFileInput } from './FormTemplateFileInput'
import { useCreateFormTemplate } from './hooks'

import './FormTemplateView.scss'

export const FormTemplateCreateView: React.FC = () => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()
  const { portalRef } = useContext(HeaderContext)

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  return (
    <>
      {portalRef?.current ? createPortal(<FormTemplateViewHeader />, portalRef.current) : null}
      {!documentSummaryEnabled ? <Upsell feature={DELTA_TRIAL_FEATURES['formTemplate']} /> : <FormTemplateCreateViewContent />}
    </>
  )
}

export const FormTemplateCreateViewContent: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()
  const { formId = '' } = useParams()
  const { tracked } = useTracking()

  const [executeSystemPreset] = useExecuteSystemPresetMutation()
  const [triggerGetFormTemplatePreview] = useGetFormTemplatePreviewMutation()

  const { data: scorecardsResponse, isLoading: scorecardLoading } = useGetAllScorecardsQuery(
    {
      accountId: accountId,
      aiFeatureIds: formId,
    },
    { refetchOnMountOrArgChange: true, skip: !formId },
  )

  const [page, setPage] = useState<'file_upload' | 'mapping_form'>('file_upload')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null)
  const [fileUploadLoading, setFileUploadLoading] = useState<boolean>(false)
  const [formTemplatePreview, setFormTemplatePreview] = useState<FormTemplatePreview | null>(null)
  const [formTemplatePreviewLoading, setFormTemplatePreviewLoading] = useState<boolean>(false)
  const [mappingSuggestion, setMappingSuggestion] = useState<Record<string, FormTemplateMappingSource> | null>(null)
  const [mappingSuggestionLoading, setMappingSuggestionLoading] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  const { uploadFile, createFormTemplate } = useCreateFormTemplate()
  const { setSafeTimeout, clearSafeTimeout } = useSafeTimeout()

  const scorecard = useMemo(() => scorecardsResponse?.data.find((scorecard) => scorecard.aiFeatureIds.includes(formId)) ?? null, [formId, scorecardsResponse])

  const uploadFormTemplatePreviewFile = useCallback(
    async (file: File) => {
      setFileUploadLoading(true)
      try {
        // TODO: set TTL for "preview" file
        const fileInfo = await uploadFile(file)
        if (!fileInfo) {
          return
        }

        setFileInfo(fileInfo)

        return fileInfo
      } catch (e) {
        console.error(e)
        return null
      } finally {
        setFileUploadLoading(false)
      }
    },
    [uploadFile],
  )

  const getFormTemplatePreview = useCallback(
    async (fileId: string) => {
      setFormTemplatePreviewLoading(true)
      try {
        const { data: formTemplatePreview } = await triggerGetFormTemplatePreview({ accountId, fileId })
        if (!formTemplatePreview) {
          return
        }

        setFormTemplatePreview(formTemplatePreview)

        return formTemplatePreview
      } catch (e) {
        console.error(e)
        return null
      } finally {
        setFormTemplatePreviewLoading(false)
      }
    },
    [accountId, triggerGetFormTemplatePreview],
  )

  const generateMappingSuggestion = useCallback(
    async (formTemplatePreview: FormTemplatePreview, scorecard: Scorecard) => {
      setMappingSuggestionLoading(true)
      try {
        const context = getContextForFormTemplateMappingSuggestion(formTemplatePreview, scorecard.descriptor as ObjectDescriptor)
        const { data: systemPresetExecutionResult } = await executeSystemPreset({
          accountId,
          payload: {
            systemPresetName: 'form-template-mapping-suggestion',
            context,
          },
        })
        if (!systemPresetExecutionResult?.length) {
          return null
        }

        const { result: aiFeatureResult } = systemPresetExecutionResult[0]
        if (!aiFeatureResult) {
          return null
        }

        const { result } = aiFeatureResult as StructuredSummaryResult
        if (!isResultValidMappingSuggestion(result)) {
          return null
        }

        setMappingSuggestion(result)

        return result
      } catch (e) {
        console.error(e)
        return null
      } finally {
        setMappingSuggestionLoading(false)
      }
    },
    [accountId, executeSystemPreset],
  )

  const resetFileUpload = () => {
    setSelectedFile(null)
    setFileInfo(null)
    setFormTemplatePreview(null)
    setMappingSuggestion(null)
  }

  const handleFileSelected = useCallback(
    async (file: File) => {
      if (!scorecard) {
        return
      }

      clearSafeTimeout()
      setSelectedFile(file)

      const fileInfo = await uploadFormTemplatePreviewFile(file)
      if (!fileInfo) {
        resetFileUpload()
        return
      }

      const formTemplatePreview = await getFormTemplatePreview(fileInfo.fileId)
      if (!formTemplatePreview) {
        resetFileUpload()
        return
      }

      const mappingSuggestion = await generateMappingSuggestion(formTemplatePreview, scorecard)
      if (!mappingSuggestion) {
        resetFileUpload()
        return
      }

      setSafeTimeout(() => setPage('mapping_form'), 1000)
    },
    [clearSafeTimeout, generateMappingSuggestion, getFormTemplatePreview, scorecard, setSafeTimeout, uploadFormTemplatePreviewFile],
  )

  const _handleSubmit = useCallback(
    async (formData: FormTemplateMappingFormValues) => {
      const { mapping } = formData
      if (!selectedFile || !formTemplatePreview || !scorecard) {
        return
      }

      const name = selectedFile.name.split('.')[0]

      setSubmitLoading(true)
      try {
        const fileInfo = await uploadFile(selectedFile)
        if (!fileInfo) {
          return
        }

        const formTemplate = await createFormTemplate(fileInfo.fileId, name, scorecard.id, formTemplatePreview.targets, mapping)
        if (!formTemplate) {
          return
        }

        navigate(`/workspace/setup/forms/form/details/${formId}/templates`)
      } catch (e) {
        console.error(e)
      } finally {
        setSubmitLoading(false)
      }
    },
    [createFormTemplate, formId, formTemplatePreview, navigate, scorecard, selectedFile, uploadFile],
  )

  const handleSubmit = tracked(
    _handleSubmit,
    { eventName: TrackEvent.FORM_TEMPLATE_CREATED },
    {
      eventDetailsExtractor: (formData: FormTemplateMappingFormValues) => {
        const { mapping } = formData
        return {
          totalFields: formTemplatePreview?.targets.length,
          fieldsMapped: Object.keys(mapping).length,
        }
      },
    },
  )

  const handleDiscard = () => {
    navigate(`/workspace/setup/forms/form/details/${formId}/templates`)
  }

  if (scorecardLoading) {
    return <Spinner />
  }

  if (page === 'file_upload') {
    return (
      <FormTemplateFileUploadPage
        selectedFile={selectedFile}
        handleFileSelected={handleFileSelected}
        fileInfo={fileInfo}
        fileUploadLoading={fileUploadLoading}
        formTemplatePreview={formTemplatePreview}
        formTemplatePreviewLoading={formTemplatePreviewLoading}
        mappingSuggestion={mappingSuggestion}
        mappingSuggestionLoading={mappingSuggestionLoading}
      />
    )
  }

  if (!formTemplatePreview || !mappingSuggestion || !scorecard) {
    return null
  }

  return (
    <FormTemplateMappingForm
      targets={formTemplatePreview.targets}
      mapping={mappingSuggestion}
      scorecardDescriptor={scorecard.descriptor}
      onSubmit={handleSubmit}
      submitLoading={submitLoading}
      handleDiscard={handleDiscard}
    />
  )
}

interface FormTemplateFileUploadPageProps {
  selectedFile: File | null
  handleFileSelected: (file: File) => Promise<void>
  fileInfo: FileInfo | null
  fileUploadLoading: boolean
  formTemplatePreview: FormTemplatePreview | null
  formTemplatePreviewLoading: boolean
  mappingSuggestion: Record<string, FormTemplateMappingSource> | null
  mappingSuggestionLoading: boolean
}

const FormTemplateFileUploadPage: React.FC<FormTemplateFileUploadPageProps> = ({
  selectedFile,
  handleFileSelected,
  fileInfo,
  fileUploadLoading,
  formTemplatePreview,
  formTemplatePreviewLoading,
  mappingSuggestion,
  mappingSuggestionLoading,
}) => {
  return (
    <div className='h-100 w-100 d-flex align-items-center justify-content-center'>
      <div className='d-flex flex-column align-items-center zocks-gap-2 form-template-create-file-upload-container'>
        <div className='d-flex flex-column align-items-center zocks-gap-1 form-template-create-file-upload-inner-container'>
          <div className='d-flex flex-column align-items-center zocks-gap-0.5'>
            <div className='py-1 zocks-px-0.75 zocks-bg-color-gray-100 border zocks-border-color-gray-200 rounded-5'>
              <span className='lead-extra-small zocks-text-gray-700'>Document Templates</span>
            </div>
            <h3>Skip manual document filling</h3>
          </div>
          <span className='paragraph-medium text-center'>
            Upload and map fields from branded PDFs to Financial Planning Data form. Your AI Assistant will fill them automatically.
          </span>
        </div>
        {!selectedFile ? (
          <>
            <FormTemplateFileInput onFileSelected={handleFileSelected} />
            <p className='form-template-create-help-textbox text-center paragraph-medium'>
              <span>To get the best results, make sure your PDF has fillable form fields.</span>
              {/*<span>To get the best results, make sure your PDF has fillable form fields. Not sure how?</span>{' '}*/}
              {/*<a href='' target='_blank' rel='noopener noreferrer' className='zocks-text-primary'>*/}
              {/*  Learn how to prepare your PDF →*/}
              {/*</a>*/}
            </p>
          </>
        ) : (
          <FileUploadProgress
            selectedFile={selectedFile}
            fileInfo={fileInfo}
            fileUploadLoading={fileUploadLoading}
            formTemplatePreview={formTemplatePreview}
            formTemplatePreviewLoading={formTemplatePreviewLoading}
            mappingSuggestion={mappingSuggestion}
            mappingSuggestionLoading={mappingSuggestionLoading}
          />
        )}
      </div>
    </div>
  )
}

interface FileUploadProgressProps {
  selectedFile: File | null
  fileInfo: FileInfo | null
  fileUploadLoading: boolean
  formTemplatePreview: FormTemplatePreview | null
  formTemplatePreviewLoading: boolean
  mappingSuggestion: Record<string, FormTemplateMappingSource> | null
  mappingSuggestionLoading: boolean
}

const FileUploadProgress: React.FC<FileUploadProgressProps> = ({
  selectedFile,
  fileInfo,
  fileUploadLoading,
  formTemplatePreview,
  formTemplatePreviewLoading,
  mappingSuggestion,
  mappingSuggestionLoading,
}) => {
  const progressItems: ProgressItem[] = useMemo(() => {
    if (!selectedFile) {
      return []
    }
    return [
      {
        isLoading: fileUploadLoading,
        isSuccess: !fileUploadLoading && !!fileInfo,
        label: fileInfo ? `Uploaded ${fileInfo.fileName}` : `Uploading ${selectedFile.name}…`,
      },
      {
        isLoading: formTemplatePreviewLoading,
        isSuccess: !formTemplatePreviewLoading && !!formTemplatePreview,
        label: formTemplatePreview ? `${formTemplatePreview.targets.length} fields found in the document` : 'Looking for fields in the document…',
      },
      {
        isLoading: mappingSuggestionLoading,
        isSuccess: !mappingSuggestionLoading && !!mappingSuggestion,
        label: mappingSuggestion ? `${Object.keys(mappingSuggestion).length} fields matched. Ready to review and adjust.` : 'Matching fields to the form…',
      },
    ]
  }, [fileInfo, fileUploadLoading, formTemplatePreview, formTemplatePreviewLoading, mappingSuggestion, mappingSuggestionLoading, selectedFile])

  return <ProcessDocumentFileUploadProgress items={progressItems} />
}
